import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>;

/**
 * Drop-in replacement for `<input type="password">` with an eye toggle on
 * the right edge that flips between hidden (dots) and revealed (text).
 * Uses `forwardRef` so react-hook-form's `register()` ref attaches to the
 * inner input unchanged; the rest of the props (id, autoComplete, value,
 * onChange, minLength, …) pass through transparently. The `type` prop is
 * forced internally — callers no longer set it.
 *
 * The toggle is `type="button"` so clicking it inside a form does not
 * submit. The aria-label swaps between "Show password" and "Hide password"
 * via the i18n keys in `common.password{Show,Hide}`.
 */
export const PasswordInput = forwardRef<HTMLInputElement, Props>(
  function PasswordInput({ className, ...rest }, ref) {
    const { t } = useTranslation();
    const [revealed, setRevealed] = useState(false);

    return (
      <div className="relative">
        <input
          ref={ref}
          {...rest}
          type={revealed ? 'text' : 'password'}
          className={`pr-10 ${className ?? ''}`}
        />
        <button
          type="button"
          onClick={() => setRevealed((v) => !v)}
          aria-pressed={revealed}
          aria-label={t(revealed ? 'common.passwordHide' : 'common.passwordShow')}
          className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 focus:outline-none focus:text-slate-700 dark:focus:text-slate-200"
        >
          {revealed ? <EyeSlashIcon /> : <EyeIcon />}
        </button>
      </div>
    );
  },
);

function EyeIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
      aria-hidden="true"
    >
      <path d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeSlashIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
      aria-hidden="true"
    >
      <path d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395" />
      <path d="M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774" />
      <path d="M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65" />
      <path d="M9.88 9.88a3 3 0 1 0 4.242 4.242" />
    </svg>
  );
}
